import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import BoxWrapper from "../../components/ui/BoxWrapper";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../hooks/useToast";
import { useMutation, useQuery } from "react-query";
import apiService from "../../service/apiService";
import { useCookie } from "../../hooks/useCookie";
import { CSpinner } from "@coreui/react";

import CreateRewardForm from "./CreateRewardForm";
import { createRewardSchema } from "../../rules/reward";
import { useRef } from "react";

interface Props {
  mode: "create" | "edit";
}

export default function AddNewReward({ mode = "create" }: Props) {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(createRewardSchema, undefined, {
      context: { mode },
    } as any),
    defaultValues: {
      promoScope: "GLOBAL",
      restrictionType: "NONE",
      event: null,
      offer: null,
      place: null,
      isFeatured: false,
    },
  });

  // console.log("🚀 ~ file: AddNewReward.tsx:100 ~ errors:", errors);
  const navigate = useNavigate();
  const { toastError, toastSuccess } = useToast();
  const { token } = useCookie("vToken");
  const params = useParams();

  const createRewardMutation = useMutation({
    mutationFn: (data: FormData) =>
      apiService.MakePostRequest("rewards", data, token),
  });

  const updateRewardMutation = useMutation({
    mutationFn: (data: FormData) =>
      apiService.MakePutRequest(`rewards/${params.id}`, data, token),
  });

  const hasInitialized = useRef(false);

  const { data: rewardData } = useQuery({
    queryKey: ["getReward", params.id],
    queryFn: () => apiService.MakeGetRequest(`rewards/${params.id}`, token),
    enabled: mode === "edit",
    onSuccess: (data: Reward) => {
      if (hasInitialized.current) return;

      const formattedDate = data.expiresAt
        ? new Date(data.expiresAt).toISOString().split("T")[0]
        : "";

      reset({
        title: data.title,
        description: data.description,
        type: data.type,
        pointsRequired: data.pointsRequired,
        isActive: data.isActive,
        isReusable: data.isReusable,
        expiresAt: formattedDate,
        discount: data.discount,
        promoScope: data.promoScope || "GLOBAL",
        restrictionType: data.restrictionType || "NONE",
        maxUses: data.maxUses,
        event: data.event,
        offer: data.offer,
        place: data.place,
        picture: data.picture, // <-- IMPORTANT
        isFeatured: data.isFeatured,
      } as any);

      hasInitialized.current = true;
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const formData = new FormData();
      // console.log("🚀 ~ file: AddNewReward.tsx:100 ~ data:", data);

      formData.append("title", data.title);
      formData.append("description", data.description || "");
      formData.append("pointsRequired", data.pointsRequired);
      formData.append("discount", data.discount || 0);
      formData.append("promoScope", data.promoScope);
      formData.append("maxUses", data.maxUses || 1);
      formData.append("expiresAt", data.expiresAt);
      formData.append("type", data.type);

      if (data.isActive !== undefined)
        formData.append("isActive", String(data.isActive));

      if (data.isReusable !== undefined)
        formData.append("isReusable", String(data.isReusable));

      if (data.isFeatured !== undefined)
        formData.append("isFeatured", String(data.isFeatured));

      if (data.restrictionType) {
        formData.append("restrictionType", data.restrictionType);
      }

      if (data.promoScope === "EVENT" && data.event?.id) {
        formData.append("eventId", data.event.id);
      }

      if (data.promoScope === "SPECIALS" && data.offer?.id) {
        formData.append("offerId", data.offer.id);
      }

      if (data.promoScope === "PLACE" && data.place?.id) {
        formData.append("placeId", data.place.id);
      }

      if (data.picture instanceof File) {
        formData.append("picture", data.picture);
      }

      if (mode === "edit") {
        await updateRewardMutation.mutateAsync(formData);
      } else {
        await createRewardMutation.mutateAsync(formData);
      }

      toastSuccess("Reward created successfully");
      navigate(-1);
    } catch (error) {
      toastError("Opération non réussie");
      console.error("Reward error:", error);
      return;
    }
  };

  return (
    <div className="px-4 rounded-4">
      <h5 className="fs-4 fw-bold mb-2">Ajouter un nouveau Bonus cart</h5>

      <BoxWrapper className="p-4 ">
        <form onSubmit={handleSubmit(onSubmit)}>
          <CreateRewardForm
            control={control}
            setValue={setValue}
            watch={watch}
            errors={errors}
            data={rewardData}
            mode={mode}
          />

          <section className="d-flex justify-content-center gap-4 mt-4">
            <button
              type="button"
              disabled={createRewardMutation.isLoading}
              onClick={() => navigate(-1)}
              className="btn btn-danger w-20 text-white"
            >
              Cancel
            </button>

            <button className="btn btn-success w-20 text-white" type="submit">
              {createRewardMutation.isLoading ? <CSpinner size="sm" /> : "Save"}
            </button>
          </section>
        </form>
      </BoxWrapper>
    </div>
  );
}
